















































































































import {
  computed,
  defineComponent,
  onMounted,
  ref,
} from '@nuxtjs/composition-api';
import { ValidationObserver, ValidationProvider, setInteractionMode, extend } from 'vee-validate';
import { max, min, required } from 'vee-validate/dist/rules';

import {
  SfButton,
  SfInput,
  SfLoader,
  SfTextarea,
} from '~/components';
import userGetters from '~/modules/customer/getters/userGetters';
import type { ProductReviewRatingMetadata } from '~/modules/GraphQL/types';
import reviewGetters from '~/modules/review/getters/reviewGetters';
import { useReview } from '~/modules/review/composables/useReview';
import { useUser } from '~/modules/customer/composables/useUser';
import { usePageStore } from '~/stores/page';
import { useUiNotification } from '~/composables';

setInteractionMode('eager');
extend('required', {
  ...required,
  message: 'This field is required',
});
extend('min', {
  ...min,
  message: 'The field should have at least {length} characters',
});
extend('max', {
  ...max,
  message: 'The field may not be greater than {length} characters',
});

const BASE_FORM = (id) => ({
  nickname: '',
  ratings: {},
  sku: id,
  summary: '',
  text: '',
});

export default defineComponent({
  name: 'ProductAddReview',
  components: {
    SfButton,
    SfInput,
    SfLoader,
    SfTextarea,
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    sku: {
      type: String,
      default: '',
    },
  },
  emits: ['add-review'],
  setup(props, { emit }) {
    const { routeData } = usePageStore();
    const { loading, loadReviewMetadata } = useReview();
    const { isAuthenticated, user } = useUser();
    const { send: sendNotification } = useUiNotification();

    const submitAttempted = ref(false);
    const isSubmitting = ref(false);
    const currentSku = computed(() => props.sku || routeData?.sku || '');
    const form = ref(BASE_FORM(currentSku.value));
    const metadata = ref<ProductReviewRatingMetadata[]>([]);
    const hoveredRatings = ref<Record<string, number>>({});

    const ratingMetadata = computed(() => reviewGetters.getReviewMetadata([...metadata.value]));
    const displayedRatingMetadata = computed(() => ratingMetadata.value.slice(0, 1));
    const selectedRatingCount = computed(() => Object.keys(form.value.ratings).length);
    const showRatingError = computed(() => submitAttempted.value && selectedRatingCount.value === 0);
    const isFormReady = computed(() => (
      selectedRatingCount.value > 0
      && form.value.nickname.trim().length >= 2
      && form.value.nickname.trim().length <= 50
      && form.value.text.trim().length >= 2
      && form.value.text.trim().length <= 500
    ));

    const formSubmitValue = computed(() => {
      const nickname = form.value.nickname.trim() || (
        isAuthenticated.value ? userGetters.getFullName(user.value) : ''
      );
      const summary = form.value.text.trim().slice(0, 80) || nickname;
      const ratings = Object.keys(form.value.ratings).map((key) => ({
        id: key,
        value_id: `${form.value.ratings[key]}`,
      }));

      return {
        ...form.value,
        nickname,
        sku: currentSku.value,
        summary,
        ratings,
      };
    });

    const submitForm = (reset: () => void) => async () => {
      submitAttempted.value = true;

      if (!isFormReady.value || isSubmitting.value) {
        return;
      }

      isSubmitting.value = true;

      try {
        emit('add-review', formSubmitValue.value, {
          onSuccess: () => {
            isSubmitting.value = false;
            form.value = BASE_FORM(currentSku.value);
            hoveredRatings.value = {};
            submitAttempted.value = false;
            reset();
            sendNotification({
              id: Symbol('review_success'),
              message: 'Спасибо. Отзыв отправлен на модерацию.',
              type: 'success',
              icon: 'check',
              persist: false,
            });
          },
          onError: () => {
            isSubmitting.value = false;
          },
        });
      } catch {
        isSubmitting.value = false;
      }
    };

    const handleRatingClick = (metaid, id, label) => {
      form.value.ratings[metaid] = id;
      hoveredRatings.value[metaid] = Number(label);
      form.value = { ...form.value };
    };

    const markSubmitAttempted = () => {
      submitAttempted.value = true;
    };

    const handleRatingHover = (metaid, label) => {
      hoveredRatings.value = {
        ...hoveredRatings.value,
        [metaid]: Number(label),
      };
    };

    const handleRatingLeave = (metaid) => {
      const nextHoveredRatings = { ...hoveredRatings.value };
      delete nextHoveredRatings[metaid];
      hoveredRatings.value = nextHoveredRatings;
    };

    const getSelectedRatingLabel = (metaid) => {
      const metadataItem = ratingMetadata.value.find((item) => item.id === metaid);
      const selectedId = form.value.ratings[metaid];
      return Number(metadataItem?.values.find((value) => value.id === selectedId)?.label || 0);
    };

    const isRatingActive = (metaid, label) => (
      Number(label) <= (hoveredRatings.value[metaid] || getSelectedRatingLabel(metaid))
    );

    onMounted(async () => {
      metadata.value = await loadReviewMetadata();
    });

    return {
      displayedRatingMetadata,
      form,
      formSubmitValue,
      handleRatingClick,
      handleRatingHover,
      handleRatingLeave,
      isFormReady,
      isRatingActive,
      isSubmitting,
      loading,
      markSubmitAttempted,
      ratingMetadata,
      showRatingError,
      submitForm,
    };
  },
});
